import { Injectable } from '@angular/core';

import { CommandHistory } from './command-history';
import { Observable, Subject, Subscription, merge } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  IMultipageCommandHistory,
  ICommandHistory,
  ICommand,
} from '../image-editor/domain/interfaces/command';

@Injectable({
  providedIn: 'root',
})
export class ImageCommandsService implements IMultipageCommandHistory {
  private pagesHistory: ICommandHistory[] = [];

  private readonly dirty$ = new Subject<boolean>();

  private dirtySubscription$: Subscription | null = null;

  set pageCount(value: number) {
    if (value < 0) throw new Error('Only non-negative values are allowed.');
    this.pagesHistory = [];
    while (value-- > 0) this.pagesHistory.push(new CommandHistory());
    this.currentPageIndex = this.pagesHistory.length ? 0 : -1;
    this.initDirtySubscription();
  }
  get pageCount(): number {
    return this.pagesHistory.length;
  }

  private _currentPageIndex = -1;
  set currentPageIndex(value: number) {
    this._currentPageIndex = value;
  }
  get currentPageIndex(): number {
    return this._currentPageIndex;
  }

  get currentPageHistory(): ICommandHistory | null {
    if (
      this.currentPageIndex < 0 ||
      this.currentPageIndex >= this.pagesHistory.length
    )
      return null;
    return this.pagesHistory[this.currentPageIndex];
  }

  clearAllPages(): void {
    this.pagesHistory.forEach((hist) => hist.clear());
    this.pageCount = 0;
  }

  resetAllPages(): void {
    this.pagesHistory.forEach((hist) => hist.reset());
  }

  push(command: ICommand): void {
    this.currentPageHistory?.push(command);
  }

  head(): ICommand | null {
    return this.currentPageHistory?.head() ?? null;
  }

  canUndo(): boolean {
    return this.currentPageHistory?.canUndo() ?? false;
  }

  canRedo(): boolean {
    return this.currentPageHistory?.canRedo() ?? false;
  }

  undo(): void {
    this.currentPageHistory?.undo();
  }

  redo(): void {
    this.currentPageHistory?.redo();
  }

  clear(): void {
    this.currentPageHistory?.clear();
  }

  reset(): void {
    this.currentPageHistory?.reset();
  }

  getAll(): ICommand[] {
    return this.currentPageHistory?.getAll() ?? [];
  }

  size(): number {
    return this.currentPageHistory?.size() ?? 0;
  }

  dirty(): Observable<boolean> {
    return this.dirty$;
  }

  getPageHistory(pageIndex: number): ICommandHistory {
    return this.pagesHistory[pageIndex];
  }

  private initDirtySubscription() {
    if (this.dirtySubscription$) {
      this.dirtySubscription$.unsubscribe();
      this.dirtySubscription$ = null;
    }

    if (!this.pagesHistory.length) return;

    this.dirtySubscription$ = merge(
      ...this.pagesHistory.map((hist) => hist.dirty())
    )
      .pipe(distinctUntilChanged())
      .subscribe((state) => this.dirty$.next(state as boolean));
  }
}
